import React, { useState } from "react";
import Select from "react-select";
import styles from "./Pnl.module.css";
import ToggleButton from "./ToggleButton";
import { getAnnualPnlStatement } from "../../api";
import {
  getCalendarYearOptions,
  getFinancialYearOptions,
  getFilteredMonthOptions,
  getCurrentMonthWarningText,
} from "../../Constants/helper";
import { monthOptions, typeOptions } from "../../Constants/index";
import tickOutline from "../../public/images/tickOutline.svg";
import warningOutline from "../../public/images/warningOutline.svg";
import infoOutline from "../../public/images/infoOutline.svg";

const yearOptions = [
  {
    value: "CALENDAR_YEAR",
    label: (
      <>
        Calendar Year &nbsp;
        <span className={styles.calendarYearSubLabel}>(Jan-Dec)</span>
      </>
    ),
  },
  {
    value: "FINANCIAL_YEAR",
    label: (
      <>
        Financial Year &nbsp;
        <span className={styles.calendarYearSubLabel}>(Apr-Mar)</span>
      </>
    ),
  },
];

const AnnualReport = ({ yearType, setYearType }) => {
  return (
    <div className={styles.yearContainer}>
      {yearOptions.map((option) => (
        <label className={styles.customRadio} key={option.value}>
          <input
            type="radio"
            name="yearType"
            value={option.value}
            checked={yearType === option.value}
            onChange={() => setYearType(option.value)}
            className={styles.customRadioInput}
          />
          <span className={styles.customRadioOuter}>
            <span className={styles.customRadioInner}></span>
          </span>
          <span className={styles.calendarYearLabel}>{option.label}</span>
        </label>
      ))}
    </div>
  );
};

const StatusScreen = ({ type, onClose, message }) => {
  let icon, iconContainerClass, title, description;

if (type === "error") {
    icon = warningOutline;
    iconContainerClass = styles.errorIconContainer;
    title = (
      <div className={styles.errorTitle}>
        {message || "Something went wrong"}
      </div>
    );
    if (
      message?.toLowerCase().includes("missing") ||
      message?.toLowerCase().includes("details")
    ) {
      description = (
        <p className={styles.errorDescription}>
          Please verify the customer information.
        </p>
      );
    } else {
    description = (
      <p className={styles.errorDescription}>
        We're not able to proceed with your request at the moment. Please try
        again in a bit.
      </p>
    );
  }
  } else if (type === "noData") {
    icon = infoOutline;
    iconContainerClass = styles.noDataFoundIconContainer;
    title = <h3 className={styles.successTitle}>No Results Found!</h3>;
    description = (
      <p className={styles.errorDescription}>
        No P&L data exists for the selected duration. Request you to try
        changing the duration filters.
      </p>
    );
  }

  return (
    <div className={styles.statusOverlay}>
      <div className={styles.statusModal}>
        <button className={styles.closeButton} onClick={onClose}>
          ×
        </button>
        <div className={styles.modalContent}>
          <div className={iconContainerClass}>
            <img src={icon} alt="Status Icon" className={styles.errorIcon} />
          </div>
          <div className={styles.errorMessage}>
            {title}
            {description}
          </div>
        </div>
      </div>
    </div>
  );
};

const PnlStatements = ({ custId, userData }) => {
  const [type, setType] = useState(typeOptions[0]);
  const [yearType, setYearType] = useState("CALENDAR_YEAR");
  const [periodYearOptions, setPeriodYearOptions] = useState(
    getCalendarYearOptions()
  );
  const [period, setPeriod] = useState(periodYearOptions[0]);
  const [month, setMonth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [apiData, setApiData] = useState(null);

  const resetToInitialState = () => {
    const initialType = typeOptions[0];
    setType(initialType);
    setYearType("CALENDAR_YEAR");

    const calYears = getCalendarYearOptions();
    setPeriodYearOptions(calYears);
    setPeriod(calYears[0]);
    setMonth(null);
    setStatus({ type: "", message: "" });
    setApiData(null);
  };

  const handleCloseStatus = () => {
    resetToInitialState();
  };

  const handleTypeChange = (selectedType) => {
    setType(selectedType);
    setMonth(null);

    if (selectedType === "Annual Report") {
      const years =
        yearType === "CALENDAR_YEAR"
          ? getCalendarYearOptions()
          : getFinancialYearOptions();
      setPeriodYearOptions(years);
      setPeriod(years[0]);
    } else {
      const years = getCalendarYearOptions();
      setPeriodYearOptions(years);
      setPeriod(years[0]);

      const months = getFilteredMonthOptions(monthOptions, years[0]);
      setMonth(months[months.length - 1]);
    }
  };

  const handleYearTypeChange = (selectedYearType) => {
    setYearType(selectedYearType);
    const years =
      selectedYearType === "CALENDAR_YEAR"
        ? getCalendarYearOptions()
        : getFinancialYearOptions();
    setPeriodYearOptions(years);
    setPeriod(years[0]);
  };

  const handleRun = async () => {
    if (!custId || !userData?.dwaccountNo) {
      if (!custId && !userData?.dwaccountNo) {
        setStatus({ type: "error", message: "Customer details are missing." });
      } else if (!custId) {
        setStatus({ type: "error", message: "Customer ID is missing." });
      } else if (!userData?.dwaccountNo) {
        setStatus({ type: "error", message: "Account number is missing." });
      }
      return;
    }

    setLoading(true);
    setStatus({ type: "", message: "" });

    try {
      let resp;
      if (type === "Monthly Report") {
        if (period && period.value && month && month.value) {
          const monthValue =
            month.value.length === 1 ? `0${month.value}` : month.value;
          resp = await getAnnualPnlStatement(
            "monthly",
            period.value,
            monthValue,
            custId,
            userData.dwaccountNo
          );
        } else {
          setStatus({ type: "error", message: "Please select year and month" });
          setLoading(false);
          return;
        }
      } else if (type === "Annual Report") {
        if (period && period.value) {
          const year =
            yearType === "CALENDAR_YEAR"
              ? period.value
              : period.value.substr(period.value.length - 4);
          resp = await getAnnualPnlStatement(
            "annual",
            year,
            yearType === "CALENDAR_YEAR" ? "Jan-Dec" : "Apr-Mar",
            custId,
            userData.dwaccountNo
          );
        } else {
          setStatus({ type: "error", message: "Please select period" });
          setLoading(false);
          return;
        }
      }

      if (resp && resp.data) {
        setApiData(resp);
        handleDownload(resp);
        resetToInitialState();
      } else if (resp && (resp.status === 400 || resp.status === 404)) {
        setStatus({ type: "noData", message: "No Data Found." });
      } else {
        setStatus({ type: "error", message: "Something went wrong." });
      }
    } catch (error) {
      if (
        error?.response &&
        (error.response.status === 400 || error.response.status === 404)
      ) {
        setStatus({ type: "noData", message: "No Data Found" });
      } else {
        setStatus({ type: "error", message: "Something went wrong." });
      }
    }

    setLoading(false);
  };

  const handleDownload = (data = apiData) => {
    if (!data) return;
    const blob = new Blob([data.data], {
      type: data.headers["content-type"],
    });
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = `pnl_report_${Date.now()}.xlsx`;
    link.click();
  };
  const customStyle = () => ({
    container: (provided) => ({
      ...provided,
      width: "100%",
      maxWidth: "332px",
      borderRadius: "12px",
    }),
    control: (provided, state) => ({
      ...provided,
      backgroundColor: "var(--theme-colordiv)",
      border: state.isFocused ? "1px solid #3A8DFF" : "1px solid #555",
      borderRadius: "12px",
      boxShadow: "none",
      cursor: "pointer",
      ":hover": {
        borderColor: "#3A8DFF",
      },
    }),
    option: (provided, { isFocused, isSelected }) => ({
      ...provided,
      backgroundColor: isSelected
        ? "rgba(58, 141, 255, 0.1)"
        : isFocused
        ? "rgba(58, 141, 255, 0.1)"
        : "var(--theme-colordiv)",
      color: isSelected ? "white" : "white",
      cursor: "pointer",
      fontFamily: "geomanistBook",
      fontSize: "14px",
      lineHeight: "20px",
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "var(--theme-colordiv)",
      borderRadius: "12px",
      boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
      marginTop: "4px",
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      maxHeight: "224px",
      overflowY: "auto",
      "&::-webkit-scrollbar": { width: "6px" },
      "&::-webkit-scrollbar-track": { background: "transparent" },
      "&::-webkit-scrollbar-thumb": {
        background: "#555",
        borderRadius: "4px",
      },
      "&::-webkit-scrollbar-thumb:hover": { background: "#777" },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "white",
      fontFamily: "geomanistBook",
      fontWeight: 400,
      fontSize: "14px",
      lineHeight: "20px",
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#AAA",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: "#AAA",
      ":hover": { color: "#FFF" },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 13000 }),
  });

  return (
    <div className={styles.containerPadding}>
      <div className="modal-header max-76">
        <h5 className="modal-title modalTitle">P&L Statements</h5>
      </div>
      {status.type && (
        <StatusScreen
          type={status.type}
          message={status.message}
          onClose={handleCloseStatus}
        />
      )}

      <ToggleButton
        buttons={typeOptions}
        selected={type}
        onClick={handleTypeChange}
        className={styles.toggleContainer}
        buttonClass={styles.toggleButton}
      />

      {type === "Annual Report" && (
        <>
          <AnnualReport
            yearType={yearType}
            setYearType={handleYearTypeChange}
          />
          <div className={styles.yearContainer}>
            <span className={styles.chooseTenureLabel}>Choose Tenure</span>
            <Select
              placeholder="Period"
              value={period}
              onChange={(value) => setPeriod(value)}
              options={periodYearOptions}
              styles={customStyle()}
              isSearchable={false}
              menuPosition="fixed"
              menuPortalTarget={document.body}
            />
            <div className={styles.warningText}>
              {getCurrentMonthWarningText({ period, month, type, yearType })}
            </div>
          </div>
        </>
      )}

      {type === "Monthly Report" && (
        <div className={styles.monthContainer}>
          <span className={styles.chooseTenureLabel}>Select Month</span>
          <Select
            placeholder="Month"
            value={month}
            onChange={(value) => setMonth(value)}
            options={getFilteredMonthOptions(monthOptions, period)}
            styles={customStyle()}
            isSearchable={false}
            menuPosition="fixed"
            menuPortalTarget={document.body}
          />
          <div className={styles.warningText}>
            {getCurrentMonthWarningText({ period, month, type, yearType })}
          </div>
        </div>
      )}

      <button
        className={styles.runButton}
        onClick={handleRun}
        disabled={loading}
      >
        {loading ? (
          <div
            className={styles.spin}
          />
        ) : (
          "Download Report"
        )}
      </button>
    </div>
  );
};

export default PnlStatements;
